/**
 * =============================================================================
 * SPEQ55 RFP Pro - API Client
 * =============================================================================
 *
 * This module provides a configured Axios instance for making HTTP requests
 * to the backend API. It includes:
 *
 * 1. Base URL configuration from environment variables
 * 2. Request interceptors for adding auth tokens
 * 3. Response interceptors for error handling
 * 4. Request/response logging in development
 *
 * Usage:
 * ------
 * import api from './api/client';
 *
 * // GET request
 * const response = await api.get('/rfps');
 *
 * // POST request with data
 * const response = await api.post('/rfps', { title: 'New RFP', ... });
 *
 * // With query parameters
 * const response = await api.get('/rfps', { params: { status: 'active' } });
 *
 * Error Handling:
 * ---------------
 * The client automatically handles common error scenarios:
 * - 401: Redirects to login (token expired)
 * - 403: Shows permission denied message
 * - 500: Shows generic server error message
 *
 * @module api/client
 * @author SPEQ55
 */

import axios from 'axios';

// =============================================================================
// CONFIGURATION
// =============================================================================

/**
 * API base URL from environment variables.
 * Falls back to '/api' for development proxy setup.
 */
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

/**
 * Request timeout in milliseconds.
 * Prevents hanging requests from blocking the UI.
 */
const REQUEST_TIMEOUT = 30000; // 30 seconds

// =============================================================================
// AXIOS INSTANCE
// =============================================================================

/**
 * Configured Axios instance for API requests.
 * All API calls should use this instance.
 */
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: REQUEST_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
  // Send cookies with requests (needed for session-based auth)
  withCredentials: true,
});

// =============================================================================
// REQUEST INTERCEPTORS
// =============================================================================

/**
 * Request interceptor: Add authentication token.
 * Retrieves the JWT token from localStorage and adds it to the request.
 */
api.interceptors.request.use(
  (config) => {
    // Get auth token from localStorage
    const token = localStorage.getItem('auth_token');

    // Add Authorization header if token exists
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Log request in development
    if (import.meta.env.DEV) {
    }

    return config;
  },
  (error) => {
    // Log request error
    return Promise.reject(error);
  }
);

// =============================================================================
// RESPONSE INTERCEPTORS
// =============================================================================

/**
 * Response interceptor: Handle common response scenarios.
 * - Successful responses: Return data directly
 * - Error responses: Handle based on status code
 */
api.interceptors.response.use(
  (response) => {
    // Log successful response in development
    if (import.meta.env.DEV) {
    }

    // Return the response data directly for convenience
    return response;
  },
  (error) => {
    // Extract error details
    const { response, request } = error;

    // Log error in development
    if (import.meta.env.DEV) {
    }

    // Handle specific error status codes
    if (response) {
      switch (response.status) {
        case 401:
          // Unauthorized: Token expired or invalid
          // Clear stored token and redirect to login
          localStorage.removeItem('auth_token');
          // Redirect to login page (implement based on your auth flow)
          // window.location.href = '/login';
          break;

        case 403:
          // Forbidden: User doesn't have permission
          break;

        case 404:
          // Not Found: Resource doesn't exist
          break;

        case 422:
          // Validation Error: Invalid request data
          // Error details are in response.data.errors
          break;

        case 429:
          // Too Many Requests: Rate limited
          break;

        case 500:
        case 502:
        case 503:
        case 504:
          // Server Error: Something went wrong on the server
          break;

        default:
      }
    } else if (request) {
      // Request was made but no response received (network error)
    } else {
      // Error in request configuration
    }

    // Reject the promise so calling code can handle the error
    return Promise.reject(error);
  }
);

// =============================================================================
// CONVENIENCE METHODS
// =============================================================================

/**
 * Wrapper for GET requests with simpler syntax.
 *
 * @param {string} url - API endpoint
 * @param {Object} params - Query parameters
 * @returns {Promise} Axios response promise
 */
export const get = (url, params = {}) => api.get(url, { params });

/**
 * Wrapper for POST requests.
 *
 * @param {string} url - API endpoint
 * @param {Object} data - Request body
 * @returns {Promise} Axios response promise
 */
export const post = (url, data = {}) => api.post(url, data);

/**
 * Wrapper for PUT requests.
 *
 * @param {string} url - API endpoint
 * @param {Object} data - Request body
 * @returns {Promise} Axios response promise
 */
export const put = (url, data = {}) => api.put(url, data);

/**
 * Wrapper for PATCH requests.
 *
 * @param {string} url - API endpoint
 * @param {Object} data - Request body
 * @returns {Promise} Axios response promise
 */
export const patch = (url, data = {}) => api.patch(url, data);

/**
 * Wrapper for DELETE requests.
 *
 * @param {string} url - API endpoint
 * @returns {Promise} Axios response promise
 */
export const del = (url) => api.delete(url);

/**
 * Wrapper for file upload requests.
 * Uses multipart/form-data content type.
 *
 * @param {string} url - API endpoint
 * @param {File} file - File to upload
 * @param {string} fieldName - Form field name (default: 'file')
 * @param {Function} onProgress - Optional progress callback
 * @returns {Promise} Axios response promise
 */
export const upload = (url, file, fieldName = 'file', onProgress = null) => {
  const formData = new FormData();
  formData.append(fieldName, file);

  return api.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 300000, // 5 minutes for file uploads (vs 30s default)
    onUploadProgress: onProgress
      ? (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      : undefined,
  });
};

// Export the configured Axios instance as default
export default api;
